import { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  AlertTriangle,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  X,
  Smartphone,
  Cpu,
  Zap,
} from 'lucide-react';
import {
  diagnosticsApi,
  DiagnosticEntry,
  DiagnosticsFilters,
  DiagnosticsStats,
} from '../api/client';
import { formatNumber, formatRelativeTime, cn } from '../lib/utils';
import DiagnosticDetailModal from '../components/DiagnosticDetailModal';

const PAGE_SIZE = 50;

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'metric', label: 'Metric' },
  { value: 'diagnostic', label: 'Diagnostic' },
];

const typeColors: Record<string, string> = {
  metric: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  diagnostic: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

function formatRelativeTimeISO(isoString: string): string {
  return formatRelativeTime(new Date(isoString).getTime());
}

function extractSummary(entry: DiagnosticEntry): string {
  if (!entry.payload_json) return entry.payload_type;
  try {
    const json = JSON.parse(entry.payload_json);
    if (entry.payload_type === 'diagnostic') {
      const parts: string[] = [];
      if (json.crashDiagnostics?.length) parts.push(`${json.crashDiagnostics.length} crash(es)`);
      if (json.hangDiagnostics?.length) parts.push(`${json.hangDiagnostics.length} hang(s)`);
      if (json.cpuExceptionDiagnostics?.length) parts.push(`${json.cpuExceptionDiagnostics.length} CPU exception(s)`);
      if (json.diskWriteExceptionDiagnostics?.length) parts.push(`${json.diskWriteExceptionDiagnostics.length} disk exception(s)`);
      return parts.length > 0 ? parts.join(', ') : 'No diagnostics in payload';
    }
    // Metric payload — show key stats
    const parts: string[] = [];
    if (json.applicationLaunchMetrics) parts.push('launch');
    if (json.memoryMetrics) parts.push('memory');
    if (json.cpuMetrics) parts.push('cpu');
    if (json.applicationExitMetrics) parts.push('exits');
    return parts.length > 0 ? `Metrics: ${parts.join(', ')}` : 'Metric payload';
  } catch {
    return entry.payload_type;
  }
}

export default function Diagnostics() {
  // Data state
  const [items, setItems] = useState<DiagnosticEntry[]>([]);
  const [stats, setStats] = useState<DiagnosticsStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [payloadType, setPayloadType] = useState('');
  const [page, setPage] = useState(0);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Detail modal
  const [detailEntry, setDetailEntry] = useState<DiagnosticEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: DiagnosticsFilters = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (payloadType) filters.payload_type = payloadType;

      const [listResult, statsResult] = await Promise.all([
        diagnosticsApi.list(filters),
        diagnosticsApi.stats(),
      ]);
      setItems(listResult.diagnostics);
      setTotal(listResult.total);
      setStats(statsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  }, [payloadType, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page on filter change
  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [payloadType]);

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const result = await diagnosticsApi.get(id);
      setDetailEntry(result.diagnostic);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this diagnostic entry?')) return;
    try {
      await diagnosticsApi.delete(id);
      setDetailEntry(null);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected entries?`)) return;
    try {
      await diagnosticsApi.bulk({ ids: Array.from(selectedIds), action: 'delete' });
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk delete');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.id)));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-8 h-8 text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold text-gradient">Diagnostics</h1>
            <p className="text-sm text-gray-400">MetricKit crash reports, hang diagnostics, and performance metrics</p>
          </div>
        </div>
        <button onClick={fetchData} className="btn-glow flex items-center gap-2" disabled={loading}>
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-glow p-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-gray-400">Total Payloads</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(stats.total)}</p>
            </div>
            <div className="card-glow p-4">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-rose-400" />
                <span className="text-xs text-gray-400">Crashes</span>
              </div>
              <p className="text-2xl font-bold text-rose-400">{formatNumber(stats.crashes)}</p>
            </div>
            <div className="card-glow p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-gray-400">Hangs</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{formatNumber(stats.hangs)}</p>
            </div>
            <div className="card-glow p-4">
              <div className="flex items-center gap-2 mb-1">
                <Smartphone className="w-4 h-4 text-violet-400" />
                <span className="text-xs text-gray-400">Unique Devices</span>
              </div>
              <p className="text-2xl font-bold text-violet-400">{formatNumber(stats.unique_devices)}</p>
            </div>
          </div>
          {stats.sample_limit && (
            <p className="text-xs text-muted-foreground">
              Crash and hang counts use the last {formatNumber(stats.sample_limit)} diagnostic reports ({formatNumber(stats.sampled_diagnostic_reports || 0)} valid, non-debug reports sampled).
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert-error p-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters + Bulk Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={payloadType}
          onChange={e => setPayloadType(e.target.value)}
          className="input-neural text-sm"
        >
          {TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {selectedIds.size > 0 && (
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-lg hover:bg-rose-500/30 transition"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete {selectedIds.size}
          </button>
        )}

        <span className="text-sm text-gray-500 ml-auto">
          {formatNumber(total)} total
        </span>
      </div>

      {/* Table */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No diagnostic payloads yet</p>
          <p className="text-xs mt-1">MetricKit data arrives from devices running your app</p>
        </div>
      ) : (
        <div className="card-glow overflow-hidden">
          <table className="table-neural min-w-full">
            <thead>
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === items.length && items.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Device</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">App Version</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Summary</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {items.map(item => (
                <tr
                  key={item.id}
                  className="hover:bg-white/[0.02] cursor-pointer transition"
                  onClick={() => handleViewDetail(item.id)}
                >
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                    {formatRelativeTimeISO(item.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border',
                      typeColors[item.payload_type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                    )}>
                      {item.payload_type === 'diagnostic' ? (
                        <Zap className="w-3 h-3" />
                      ) : (
                        <Cpu className="w-3 h-3" />
                      )}
                      {item.payload_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400 font-mono">
                    {item.device_model || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {item.app_version || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400 max-w-xs truncate">
                    {extractSummary(item)}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-gray-500 hover:text-rose-400 transition p-1"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn-secondary flex items-center gap-1 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="btn-secondary flex items-center gap-1 disabled:opacity-30"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <DiagnosticDetailModal
        entry={detailEntry}
        loading={detailLoading}
        onClose={() => setDetailEntry(null)}
        onDelete={handleDelete}
      />
    </div>
  );
}
